import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { ArrowLeft, Moon, Sun, Settings as SettingsIcon, Check, Compass, Sparkles, Eye, Gift, Copy, Users, Bell, BellOff, Type } from "lucide-react";
import { API, useAuth } from "@/App";
import axios from "axios";
import { toast } from "sonner";
import { pushSupported, subscribePush, unsubscribePush, sendTestPush } from "@/lib/push";
import { QuietDaysCard } from "@/components/QuietDaysCard";
import { ContactPrefsCard } from "@/components/ContactPrefsCard";

const SettingsPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { token, user } = useAuth();
  
  const [darkMode, setDarkMode] = useState(false);
  const [voicePreference, setVoicePreference] = useState("witness");
  const [isSaving, setIsSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [notificationsEnabled, setNotificationsEnabled] = useState(() => {
    if (!('Notification' in window)) return false;
    return Notification.permission === 'granted' && localStorage.getItem('notifications_enabled') !== 'false';
  });
  const [reminderTimes, setReminderTimes] = useState({ morning_time: "07:00", evening_time: "20:00" });
  
  // Referral state
  const [referralCode, setReferralCode] = useState(null);
  const [referralStats, setReferralStats] = useState(null);
  const [referralCopied, setReferralCopied] = useState(false);
  const [activeTab, setActiveTab] = useState(searchParams.get('tab') || 'settings');
  const [textSize, setTextSize] = useState(() => localStorage.getItem('text_size') || 'comfy');

  const handleTextSize = (size) => {
    setTextSize(size);
    localStorage.setItem('text_size', size);
    document.documentElement.classList.toggle('text-large', size === 'large');
  };

  const voiceOptions = [
    {
      id: "architect",
      name: "The Architect",
      description: "Structured, pattern-seeking, and clear",
      icon: Compass,
      tone: "Direct and analytical",
      keywords: ["Clarity", "Patterns", "Integration", "Action"]
    },
    {
      id: "alchemist",
      name: "The Alchemist",
      description: "Deep, transformative, and evocative",
      icon: Sparkles,
      tone: "Warm and transformative",
      keywords: ["Alchemy", "Presence", "Grace", "Transformation"]
    },
    {
      id: "witness",
      name: "The Witness",
      description: "Neutral, observant, and grounded",
      icon: Eye,
      tone: "Grounded and balanced",
      keywords: ["Perspective", "Truth", "Growth", "Integration"]
    }
  ];

  useEffect(() => {
    fetchPreferences();
    fetchReferralData();
    syncPushState();
    // Check if dark mode is already active
    const isDark = document.documentElement.classList.contains('dark');
    setDarkMode(isDark);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- run once on mount
  }, []);

  const syncPushState = async () => {
    try {
      const res = await axios.get(`${API}/push/status`, { headers: { Authorization: `Bearer ${token}` } });
      setReminderTimes({ morning_time: res.data.morning_time, evening_time: res.data.evening_time });
      if (res.data.opted_out) {
        setNotificationsEnabled(false);
        return;
      }
      if (!pushSupported() || !('Notification' in window) || Notification.permission !== 'granted') return;
      if (localStorage.getItem('notifications_enabled') === 'false') return;
      if (!res.data.subscribed) {
        await subscribePush(token);
      }
      setNotificationsEnabled(true);
    } catch (e) {
      console.error("Push state sync failed:", e);
    }
  };

  const saveReminderTimes = async (updated) => {
    setReminderTimes(updated);
    try {
      await axios.post(`${API}/push/reminder-times`, updated, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(`Reminders set — ${updated.morning_time} nudge & ${updated.evening_time} practice`);
    } catch (e) {
      toast.error("Couldn't save reminder times");
    }
  };

  const fetchPreferences = async () => {
    try {
      const response = await axios.get(`${API}/user/preferences`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDarkMode(response.data.dark_mode);
      setVoicePreference(response.data.voice_preference || 'witness');
      
      // Apply dark mode immediately
      if (response.data.dark_mode) {
        document.documentElement.classList.add('dark');
      }
    } catch (error) {
      console.error("Error fetching preferences:", error);
    }
  };

  const fetchReferralData = async () => {
    try {
      const [codeRes, statsRes] = await Promise.all([
        axios.get(`${API}/referrals/code`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/referrals/stats`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      setReferralCode(codeRes.data.code);
      setReferralStats(statsRes.data);
    } catch (error) {
      console.error("Error fetching referral data:", error);
    }
  };

  const copyReferralCode = () => {
    if (referralCode) {
      navigator.clipboard.writeText(referralCode);
      setReferralCopied(true);
      toast.success("Referral code copied!");
      setTimeout(() => setReferralCopied(false), 3000);
    }
  };

  const handleNotificationsToggle = async (enabled) => {
    if (!('Notification' in window)) {
      toast.error("Your browser doesn't support notifications");
      return;
    }
    if (enabled) {
      let permission = Notification.permission;
      if (permission === 'default') {
        permission = await Notification.requestPermission();
      }
      if (permission === 'granted') {
        localStorage.setItem('notifications_enabled', 'true');
        setNotificationsEnabled(true);
        if (pushSupported()) {
          try {
            await subscribePush(token);
            toast.success("Reminders on — 7am rhythm nudge & 8pm evening practice, even with the tab closed 🔔");
          } catch (e) {
            console.error("Push subscribe failed:", e);
            toast.success("Evening practice reminders are on 🔔 (closed-tab push unavailable on this browser)");
          }
        } else {
          toast.success("Evening practice reminders are on 🔔");
        }
      } else {
        setNotificationsEnabled(false);
        toast.error("Notifications are blocked in your browser settings. Allow them for this site to enable reminders.");
      }
    } else {
      localStorage.setItem('notifications_enabled', 'false');
      setNotificationsEnabled(false);
      if (pushSupported()) {
        try {
          await unsubscribePush(token);
        } catch (e) {
          console.error("Push unsubscribe failed:", e);
        }
      }
      toast.success("Reminders turned off");
    }
  };

  const handleTestPush = async () => {
    try {
      const res = await sendTestPush(token);
      if (res.sent > 0) {
        toast.success("Test notification sent — check your device!");
      } else {
        toast.error(res.message || "No push subscription yet — toggle reminders on first");
      }
    } catch (e) {
      toast.error("Couldn't send test notification");
    }
  };

  const handleDarkModeToggle = async (enabled) => {
    setDarkMode(enabled);
    
    // Apply immediately
    if (enabled) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
    
    // Save to backend
    await savePreferences(enabled, voicePreference);
  };

  const handleVoiceChange = async (voice) => {
    setVoicePreference(voice);
    await savePreferences(darkMode, voice);
  };

  const savePreferences = async (dark, voice) => {
    setIsSaving(true);
    setSaved(false);
    
    try {
      await axios.put(`${API}/user/preferences`, {
        dark_mode: dark,
        voice_preference: voice
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Store in localStorage for persistence
      localStorage.setItem('darkMode', dark);
      localStorage.setItem('voicePreference', voice);
      
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } catch (error) {
      console.error("Error saving preferences:", error);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="min-h-screen hero-gradient" data-testid="settings-page">
      {/* Navigation */}
      <nav className="sticky top-0 z-50 bg-background/80 backdrop-blur-md border-b border-border/50">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center justify-between">
          <Button
            variant="ghost"
            onClick={() => navigate("/dashboard")}
            className="text-muted-foreground hover:text-foreground"
            data-testid="back-btn"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Dashboard
          </Button>
          <div className="flex items-center gap-2">
            <SettingsIcon className="w-5 h-5 text-primary" strokeWidth={1.5} />
            <span className="font-heading text-lg font-semibold">Settings & Profile</span>
          </div>
          <div className="w-24">
            {saved && (
              <span className="text-sm text-accent flex items-center gap-1">
                <Check className="w-4 h-4" /> Saved
              </span>
            )}
          </div>
        </div>
      </nav>

      {/* Tab Navigation */}
      <div className="max-w-2xl mx-auto px-6 pt-6">
        <div className="flex gap-2 mb-6">
          <Button
            variant={activeTab === 'settings' ? 'default' : 'outline'}
            onClick={() => setActiveTab('settings')}
            className="rounded-full"
            data-testid="settings-tab"
          >
            <SettingsIcon className="w-4 h-4 mr-2" />
            Settings
          </Button>
          <Button
            variant={activeTab === 'referrals' ? 'default' : 'outline'}
            onClick={() => setActiveTab('referrals')}
            className="rounded-full"
            data-testid="referrals-tab"
          >
            <Gift className="w-4 h-4 mr-2" />
            Referrals
          </Button>
        </div>
      </div>

      <main className="max-w-2xl mx-auto px-6 pb-8">
        {/* Referrals Tab */}
        {activeTab === 'referrals' && (
          <div className="space-y-6">
            {/* Referral Code Card */}
            <Card className="glass-card rounded-3xl border-0 bg-gradient-to-br from-amber-50/50 to-orange-50/50 dark:from-amber-950/20 dark:to-orange-950/20" data-testid="referral-code-card">
              <CardContent className="p-8">
                <div className="text-center mb-6">
                  <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-amber-500 to-orange-500 flex items-center justify-center mx-auto mb-4">
                    <Gift className="w-8 h-8 text-white" strokeWidth={1.5} />
                  </div>
                  <h2 className="font-heading text-2xl font-bold text-foreground mb-2">
                    Refer a Friend
                  </h2>
                  <p className="text-muted-foreground">
                    Share your unique code. You both get <strong className="text-amber-600">2 weeks free</strong> premium!
                  </p>
                </div>

                {referralCode && (
                  <div className="bg-white/70 dark:bg-gray-800/70 rounded-2xl p-6 text-center mb-6">
                    <p className="text-sm text-muted-foreground mb-2">Your Referral Code</p>
                    <div className="flex items-center justify-center gap-3">
                      <code className="text-3xl font-mono font-bold text-amber-700 dark:text-amber-400">
                        {referralCode}
                      </code>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={copyReferralCode}
                        className="border-amber-300 text-amber-700 hover:bg-amber-100"
                        data-testid="copy-code-btn"
                      >
                        {referralCopied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                      </Button>
                    </div>
                  </div>
                )}

                {/* Stats */}
                {referralStats && (
                  <div className="grid grid-cols-3 gap-4 text-center">
                    <div className="bg-white/50 dark:bg-gray-800/50 rounded-xl p-4">
                      <p className="text-2xl font-bold text-foreground">{referralStats.total_referrals}</p>
                      <p className="text-xs text-muted-foreground">Friends Referred</p>
                    </div>
                    <div className="bg-white/50 dark:bg-gray-800/50 rounded-xl p-4">
                      <p className="text-2xl font-bold text-foreground">{referralStats.successful_referrals}</p>
                      <p className="text-xs text-muted-foreground">Successful</p>
                    </div>
                    <div className="bg-white/50 dark:bg-gray-800/50 rounded-xl p-4">
                      <p className="text-2xl font-bold text-amber-600">{referralStats.total_reward_days}</p>
                      <p className="text-xs text-muted-foreground">Days Earned</p>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Referral History */}
            {referralStats?.referrals?.length > 0 && (
              <Card className="glass-card rounded-3xl border-0" data-testid="referral-history-card">
                <CardContent className="p-6">
                  <h3 className="font-heading text-lg font-semibold text-foreground mb-4 flex items-center gap-2">
                    <Users className="w-5 h-5 text-primary" />
                    Referral History
                  </h3>
                  <div className="space-y-3">
                    {referralStats.referrals.map((ref, idx) => (
                      <div key={ref.id || `${ref.name}-${ref.joined_at}-${idx}`} className="flex items-center justify-between py-3 border-b border-border/50 last:border-0">
                        <div>
                          <p className="font-medium text-foreground">{ref.name}</p>
                          <p className="text-xs text-muted-foreground">
                            Joined {new Date(ref.joined_at).toLocaleDateString()}
                          </p>
                        </div>
                        <span className={`text-xs px-2 py-1 rounded-full ${
                          ref.status === 'completed' 
                            ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400' 
                            : 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400'
                        }`}>
                          {ref.status === 'completed' ? '+14 days earned' : 'Pending'}
                        </span>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* How it Works */}
            <Card className="glass-card rounded-3xl border-0" data-testid="how-it-works-card">
              <CardContent className="p-6">
                <h3 className="font-heading text-lg font-semibold text-foreground mb-4">
                  How It Works
                </h3>
                <div className="space-y-4">
                  <div className="flex items-start gap-3">
                    <div className="w-8 h-8 rounded-full bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center flex-shrink-0">
                      <span className="text-amber-700 dark:text-amber-400 font-bold text-sm">1</span>
                    </div>
                    <div>
                      <p className="font-medium text-foreground">Share your code</p>
                      <p className="text-sm text-muted-foreground">Copy and share your unique referral code with friends</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-8 h-8 rounded-full bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center flex-shrink-0">
                      <span className="text-amber-700 dark:text-amber-400 font-bold text-sm">2</span>
                    </div>
                    <div>
                      <p className="font-medium text-foreground">Friend signs up</p>
                      <p className="text-sm text-muted-foreground">They enter your code during registration</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-8 h-8 rounded-full bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center flex-shrink-0">
                      <span className="text-amber-700 dark:text-amber-400 font-bold text-sm">3</span>
                    </div>
                    <div>
                      <p className="font-medium text-foreground">You both win!</p>
                      <p className="text-sm text-muted-foreground">Both of you get 2 weeks of free premium access</p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Settings Tab */}
        {activeTab === 'settings' && (
          <>
            {/* Dark Mode Toggle */}
            <Card className="glass-card rounded-3xl border-0 mb-6" data-testid="dark-mode-card">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-2xl bg-primary/10 flex items-center justify-center">
                  {darkMode ? (
                    <Moon className="w-6 h-6 text-primary" strokeWidth={1.5} />
                  ) : (
                    <Sun className="w-6 h-6 text-primary" strokeWidth={1.5} />
                  )}
                </div>
                <div>
                  <h3 className="font-heading text-lg font-semibold text-foreground">
                    Dark Mode
                  </h3>
                  <p className="text-sm text-muted-foreground">
                    {darkMode ? "Calming dark theme active" : "Switch to a calming dark theme"}
                  </p>
                </div>
              </div>
              <Switch
                checked={darkMode}
                onCheckedChange={handleDarkModeToggle}
                data-testid="dark-mode-toggle"
                aria-label="Toggle dark mode"
              />
            </div>
          </CardContent>
        </Card>

        {/* Text Size */}
        <Card className="glass-card rounded-3xl border-0 mb-6" data-testid="text-size-card">
          <CardContent className="p-6">
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <div className="flex items-center gap-4 min-w-0">
                <div className="w-12 h-12 rounded-2xl bg-primary/10 flex items-center justify-center flex-shrink-0">
                  <Type className="w-6 h-6 text-primary" strokeWidth={1.5} />
                </div>
                <div className="min-w-0">
                  <h3 className="font-heading text-lg font-semibold text-foreground">
                    Text Size
                  </h3>
                  <p className="text-sm text-muted-foreground">
                    {textSize === 'large' ? "Larger text for easier reading" : "Comfortable reading size"}
                  </p>
                </div>
              </div>
              <div className="flex rounded-full border border-border overflow-hidden flex-shrink-0 ml-auto">
                <button
                  onClick={() => handleTextSize('comfy')}
                  className={`px-4 py-2 text-sm font-semibold transition-colors ${textSize === 'comfy' ? 'bg-primary text-white' : 'text-muted-foreground'}`}
                  data-testid="text-size-comfy-btn"
                >
                  Comfy
                </button>
                <button
                  onClick={() => handleTextSize('large')}
                  className={`px-4 py-2 text-base font-semibold transition-colors ${textSize === 'large' ? 'bg-primary text-white' : 'text-muted-foreground'}`}
                  data-testid="text-size-large-btn"
                >
                  Large
                </button>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Push Notifications Toggle */}
        <Card className="glass-card rounded-3xl border-0 mb-6" data-testid="notifications-card">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-2xl bg-amber-100 flex items-center justify-center">
                  {notificationsEnabled ? (
                    <Bell className="w-6 h-6 text-amber-600" strokeWidth={1.5} />
                  ) : (
                    <BellOff className="w-6 h-6 text-muted-foreground" strokeWidth={1.5} />
                  )}
                </div>
                <div>
                  <h3 className="font-heading text-lg font-semibold text-foreground">
                    Push Notifications
                  </h3>
                  <p className="text-sm text-muted-foreground">
                    {notificationsEnabled ? `${reminderTimes.morning_time} rhythm nudge & ${reminderTimes.evening_time} evening practice — even when the tab is closed` : "Morning rhythm nudge + evening practice reminder, even when the tab is closed"}
                  </p>
                </div>
              </div>
              <Switch
                checked={notificationsEnabled}
                onCheckedChange={handleNotificationsToggle}
                data-testid="notifications-toggle"
                aria-label="Toggle push notifications"
              />
            </div>
            {notificationsEnabled && (
              <>
                <div className="mt-4 grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs font-medium text-muted-foreground block mb-1">Morning rhythm nudge</label>
                    <input
                      type="time"
                      className="w-full rounded-lg border border-input bg-background px-3 py-2 text-sm"
                      value={reminderTimes.morning_time}
                      onChange={(e) => saveReminderTimes({ ...reminderTimes, morning_time: e.target.value })}
                      data-testid="morning-time-input"
                    />
                  </div>
                  <div>
                    <label className="text-xs font-medium text-muted-foreground block mb-1">Evening practice reminder</label>
                    <input
                      type="time"
                      className="w-full rounded-lg border border-input bg-background px-3 py-2 text-sm"
                      value={reminderTimes.evening_time}
                      onChange={(e) => saveReminderTimes({ ...reminderTimes, evening_time: e.target.value })}
                      data-testid="evening-time-input"
                    />
                  </div>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  className="mt-4 rounded-full"
                  onClick={handleTestPush}
                  data-testid="test-push-btn"
                >
                  <Bell className="w-4 h-4 mr-2" />
                  Send test notification
                </Button>
              </>
            )}
          </CardContent>
        </Card>

        {/* Quiet Days */}
        <QuietDaysCard token={token} />

        {/* Contact Preferences */}
        <ContactPrefsCard token={token} />

        {/* Spirit AI Voice Selection */}
        <div className="mb-6">
          <h2 className="font-heading text-xl font-semibold text-foreground mb-2">
            Spirit AI Voice
          </h2>
          <p className="text-muted-foreground mb-6">
            Choose how Spirit speaks to you during your conversations
          </p>
          
          <div className="space-y-4">
            {voiceOptions.map((option) => {
              const Icon = option.icon;
              const isSelected = voicePreference === option.id;
              
              return (
                <Card
                  key={option.id}
                  className={`rounded-2xl border-2 cursor-pointer transition-all ${
                    isSelected 
                      ? "border-primary bg-primary/5" 
                      : "glass-card border-transparent hover:border-primary/30"
                  }`}
                  onClick={() => handleVoiceChange(option.id)}
                  data-testid={`voice-${option.id}`}
                >
                  <CardContent className="p-5">
                    <div className="flex items-start gap-4">
                      <div className={`w-12 h-12 rounded-2xl flex items-center justify-center ${
                        isSelected ? "bg-primary/20" : "bg-muted"
                      }`}>
                        <Icon className={`w-6 h-6 ${isSelected ? "text-primary" : "text-muted-foreground"}`} />
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <h3 className="font-heading text-lg font-semibold text-foreground">
                            {option.name}
                          </h3>
                          {isSelected && (
                            <span className="text-xs bg-primary text-primary-foreground px-2 py-0.5 rounded-full">
                              Active
                            </span>
                          )}
                        </div>
                        <p className="text-sm text-muted-foreground mt-1 mb-2">
                          {option.description}
                        </p>
                        <p className="text-xs text-primary mb-2">Tone: {option.tone}</p>
                        <div className="flex flex-wrap gap-2">
                          {option.keywords.map((keyword) => (
                            <span 
                              key={keyword}
                              className="text-xs bg-muted text-muted-foreground px-2 py-1 rounded-full"
                            >
                              {keyword}
                            </span>
                          ))}
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>

        {/* Info Note */}
        <div className="text-center text-sm text-muted-foreground mt-8">
          <p>Your preferences are saved automatically and sync across devices</p>
        </div>
          </>
        )}
      </main>
    </div>
  );
};

export default SettingsPage;
